import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { SocialPlatform } from '@core/enums';
import { User } from '@core/models';
import { Ad } from '@core/models/ad.model';
import { environment } from '@environments/environment';
import { ApiService } from '@shared/services/api.service';
import { AuthService } from '@shared/services/auth.service';
import { LocaleService } from '@shared/services/locale.service';
import { SnackService } from '@shared/services/snack.service';
import { TimeService } from '@shared/services/time.service';
import { ViewportService } from '@shared/services/viewport.service';
import { ButtonType } from '../button/button.component';
import { DeviceDetectorService } from 'ngx-device-detector';
import { MatDialog } from '@angular/material/dialog';
import { AdsService } from '@shared/services/ads.service';
import { Observable } from 'rxjs';
import { FullscreenCarouselDialog } from 'src/app/routes/ads/ad/ad.component';
import { NgbCarousel } from '@ng-bootstrap/ng-bootstrap/carousel/carousel.module';
import { ScrollService } from '@shared/services/scroll.service';

interface ItemProperty {
	icon: string;
	descriptionItem: string;
}

@Component({
	selector: 'app-ad-item',
	templateUrl: './ad-item.component.html',
	styleUrls: ['./ad-item.component.scss'],
})
export class AdItemComponent implements OnInit {
	constructor(
		public readonly localeService: LocaleService,
		public readonly timeService: TimeService,
		public readonly viewportService: ViewportService,
		private readonly authService: AuthService,
		private readonly apiService: ApiService,
		private readonly snackService: SnackService,
		private readonly router: Router,
		private readonly deviceDetectorService: DeviceDetectorService,
		private readonly matDialog: MatDialog,
		private readonly ads: AdsService,
		private route: ActivatedRoute,
		private scroll: ScrollService
	) {}

	@Input() ad: Ad;
	@Input() toggleCarouselPopup = false;
	@Input() isDisabledGotoAdDetail = false;
	@Input() isHotPage = false;
	public cache: Map<string, Observable<any>> = new Map();

	user: User;
	toggleFullPhoneNumber = false;

	readonly buttonType = ButtonType;
	readonly socialPlatform = SocialPlatform;

	catID: string;
	lang: string;
	itemProperties: ItemProperty[];
	@ViewChild('carousel') carousel: NgbCarousel;

	onOpenGallery(event, ad: Ad): void {
		if (ad.images.length) {
			// 	this.matDialog.open(PhotoGalleryViewComponent, {
			// 		data: {
			// 			images: ad.images,
			// 			ad: ad,
			// 		},
			// 		maxWidth: '100vw',
			// 		width: '796px',
			// 		height: '356px',
			// 	});
			// }

			const fsRef = this.matDialog.open(FullscreenCarouselDialog, {
				maxWidth: '100vw',
				width: this.viewportService.type !== 'mobile' ? '70%' : '100%',
				height: this.viewportService.type !== 'mobile' ? '85%' : '100%',
				data: {
					images: this.ad['images'],
					slideID: 0,
					ad: ad,
				},
			});
		}

		event.stopPropagation();
	}

	onWatch(ad: Ad): void {
		if (this.user) {
			this.apiService
				.watch({
					lang: this.localeService.lang,
					userID: this.user.userID,
					adID: ad.id,
					watch: ad.watching === '1' ? '0' : '1',
				})
				.subscribe((response: any) => {
					if (response.status === '1') {
						this.ad = {
							...ad,
							watching: ad.watching === '1' ? '0' : '1',
						};

						this.snackService.open(response?.message);
					}
				});
		} else {
			this.snackService.open(this.localeService.translations['ads']['s1']);
		}
	}

	/**
	 * NOTE: Go to Ad detail page is disabled on Hot/VIP list page on mobile view
	 */
	onGotoAdDetail(event: any, ad: Ad): void {
		this.scroll.save(window.scrollX, window.scrollY, this.router.url);

		if (event.target.className !== 'carousel-control-next-icon' && event.target.className !== 'carousel-control-prev-icon') {
			this.router.navigate(['/ad', ad.id]);
		}
	}

	onCall(ad: Ad): void {
		if (ad['paymentHold'] === 1 || ad['seller']['id'] === this.user?.['userID']) {
			this.toggleFullPhoneNumber = false;
			this.callApi(ad, 0);
			if (ad['seller']['id'] !== this.user?.['userID']) {
				this.snackService.open(this.localeService.translations['ads']['ad']['n662']);
			}
			return;
		}

		this.toggleFullPhoneNumber = true;
		//make direct call only on mobile
		if (this.viewportService.type === 'mobile') {
			window.location.href = `tel:${ad.phone}`;
		}
		this.callApi(ad, 0);
	}

	callApi(ad, isWhatsapp) {
		this.apiService
			.call({
				lang: this.localeService.lang,
				adId: ad.id,
				platform: 'Web',
				phone: ad.phone,
				callType: isWhatsapp ? 1 : 0,
			})
			.subscribe((res) => {
				if (res.status === '0') {
					this.snackService.open(res.message);
				} else {
					const formData: FormData = new FormData();
					formData.append('userID', this.user?.['userID']);
					formData.append('catID', this.catID);
					formData.append('lang', this.lang);

					this.apiService.ads(formData, this.router.url).subscribe((response) => {
						let data = response['response']['data'];
						const index = data.findIndex((cachedAd: Ad) => cachedAd.id === ad.id);

						const cacheKey = this.apiService.getCacheKey(this.router.url, formData);

						if (index !== -1) {
							data[index].callsCount++;
							this.cache.set(cacheKey, response);
						}
					});
				}
			});
	}

	onCallPlatform(ad: Ad): void {
		if (ad['paymentHold'] === 1 || ad['seller']['id'] === this.user?.['userID']) {
			this.callApi(ad, 1);
			if (ad['seller']['id'] !== this.user?.['userID']) {
				this.snackService.open(this.localeService.translations['ads']['ad']['n662']);
			}
			return;
		}

		const phonePrefix = ad?.phonePrefix ? ad.phonePrefix.substring(1) : '964';
		const phoneNumber = ad.phone.substring(1);
		const templateMessage = encodeURIComponent(this.localeService.translations['ads']['ad']['n418'] + '\n' + ad.adLink);
		const targetDisplay = this.deviceDetectorService.isMobile() ? '_self' : '_blank';

		window.open(`${environment.WHATSAPP.callForDetails}${phonePrefix}${phoneNumber}&text=${templateMessage}`, targetDisplay);

		this.callApi(ad, 1);
	}

	private initDeclarations(): void {
		this.user = this.authService.user();

		//TODO change the OR condition
		this.itemProperties = [
			{
				icon: 'security',
				descriptionItem: this.ad?.['motorMake'],
			},
			{
				icon: 'directions_car',
				descriptionItem: this.ad?.['motorModel'],
			},
			{
				icon: 'today',
				descriptionItem: this.ad?.['motorYear'],
			},
			{
				icon: 'place',
				descriptionItem: this.ad['location'],
			},
		];
	}

	ngOnInit(): void {
		this.initDeclarations();
		this.route.params.subscribe((params: Params) => {
			this.catID = params?.category;
			this.lang = params?.lang;
		});
	}
}
